import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { Skeleton } from '@gto/ui/skeleton'
import { Typography } from '@gto/ui/typography'

import { meQueryOptions } from '@/auth/me'
import { clearAdminSession } from '@/utils/session'

export const AdminHeader = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const meQuery = useQuery(meQueryOptions())
  // Errors are captured centrally via queryClient.queryCache.onError — see
  // src/query-client.ts. Don't duplicate the side-effect per page.

  const handleLogout = () => {
    // Clear the credentials — the in-memory access token plus the refresh in
    // localStorage, so the next boot's `bootstrap()` doesn't resurrect the
    // session — then purge the query cache so a subsequent sign-in on the same
    // tab can't briefly display the previous user's cached data (email,
    // gamemode list, etc.) before each query refetches.
    clearAdminSession()
    queryClient.clear()
    void navigate({ to: '/login' })
  }

  return (
    <header
      className="flex flex-row items-center justify-between gap-2 rounded-md bg-paper-elevation1 px-4 py-2"
      data-tst="admin-header"
    >
      <Typography
        className="font-semibold"
        color="text.primary"
        variant="subtitle1"
      >
        Admin
      </Typography>
      <div className="flex flex-row items-center gap-3">
        {meQuery.isPending && (
          <Skeleton
            className="h-6 w-[180px]"
            data-tst="admin-header-email-loading"
          />
        )}
        {meQuery.data !== undefined && (
          <Typography
            color="text.secondary"
            data-tst="admin-header-email"
            variant="body2"
          >
            {meQuery.data.email}
          </Typography>
        )}
        <Button
          data-tst="admin-header-logout"
          size="small"
          variant="outlined"
          onClick={handleLogout}
        >
          Logout
        </Button>
      </div>
    </header>
  )
}
